import { useEffect, useState } from 'react';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core/stores';
import { getContentById, getPage } from '@/services/platform/data/cms';
import {
  ShopShoppingBagApiElementContentModelContentType,
  type ApiBlockItemModel,
  type IApiContentResponseModel,
  type PickupPointContentResponseModel,
  type ShopShoppingBagApiElementContentModel,
  type ShopShoppingBagApiPropertiesModel,
} from '@/services/platform/api/model';

export interface UseShoppingBagContentResult {
  /** Properties of the CMS `shopShoppingBagApi` module (labels, useful info blocks). */
  properties?: ShopShoppingBagApiPropertiesModel;
  /** Pickup points picked on the CMS module, typed to their concrete content model. */
  pickupPoints: PickupPointContentResponseModel[];
  isLoading: boolean;
}

const cache = new Map<string, ShopShoppingBagApiPropertiesModel | undefined>();
const inflight = new Map<string, Promise<ShopShoppingBagApiPropertiesModel | undefined>>();

async function fetchBagModuleProperties(
  path: string,
  locale: string
): Promise<ShopShoppingBagApiPropertiesModel | undefined> {
  const key = `${locale}:${path}`;
  if (cache.has(key)) return cache.get(key);
  const pending = inflight.get(key);
  if (pending) return pending;

  const request = (async () => {
    const page = await getPage<IApiContentResponseModel>(path, locale, false);
    const blocks =
      ((page?.properties as { components?: { items?: ApiBlockItemModel[] } } | undefined)?.components
        ?.items ?? []) as ApiBlockItemModel[];
    const module = blocks
      .map(block => block.content as ShopShoppingBagApiElementContentModel | undefined)
      .find(
        content =>
          content?.contentType === ShopShoppingBagApiElementContentModelContentType.shopShoppingBagApi
      );

    const properties = module?.properties;
    // The Delivery API returns pickupPoints as picker stubs (id/name/route only,
    // empty `properties`) — hydrate each by id to get displayName/location/etc.
    const pickupStubs = properties?.pickupPoints ?? [];
    if (pickupStubs.length > 0) {
      const hydrated = await Promise.all(
        pickupStubs.map(stub => (stub.id ? getContentById(stub.id, locale) : undefined))
      );
      properties!.pickupPoints = hydrated.filter(Boolean) as IApiContentResponseModel[];
    }

    cache.set(key, properties);
    inflight.delete(key);
    return properties;
  })();

  inflight.set(key, request);
  return request;
}

/**
 * Fetches the CMS shopping-bag page (route from `appSettings.properties.shopShoppingBag`)
 * and exposes the `shopShoppingBagApi` module properties — bag/checkout labels, the
 * `usefulInformation` block list, and the picked `pickupPoints`. Cached per locale for
 * the app session; consumers keep i18n fallbacks for every label.
 */
export function useShoppingBagContent(): UseShoppingBagContentResult {
  const { appSettings } = useApp();
  const locale = useLocaleStore(state => state.locale);
  const path = appSettings?.properties?.shopShoppingBag?.route?.path;

  const [properties, setProperties] = useState<ShopShoppingBagApiPropertiesModel | undefined>(
    path ? cache.get(`${locale}:${path}`) : undefined
  );
  const [isLoading, setIsLoading] = useState(Boolean(path) && !cache.has(`${locale}:${path}`));

  useEffect(() => {
    if (!path || !locale) {
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    fetchBagModuleProperties(path, locale)
      .then(result => {
        if (!active) return;
        setProperties(result);
        setIsLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [path, locale]);

  return {
    properties,
    pickupPoints: (properties?.pickupPoints ?? []) as PickupPointContentResponseModel[],
    isLoading,
  };
}
